import { sanitize } from "./redact";
import { diagnosticsStore } from "./store";

// Vanilla singleton collector: a bounded ring buffer of diagnostic events with
// a common envelope. Works outside React (window listeners, fetch wrapper,
// intervals). Transport drains and ships the buffer; the store holds the few
// UI-facing counters.

const BUFFER_CAP = 200;
const BATCH_TRIGGER = 20;

// Events that should flush immediately rather than wait for the interval.
const CRITICAL_TYPES = new Set([
  "js_error",
  "unhandled_rejection",
  "server_timeout",
  "doc_scan_failed",
  "dom_injection",
  "tamper_globals",
  "support_requested",
  "session_end",
  "step_exit",
]);

let config = { validationId: null, sessionId: null, platform: "web", appVersion: null, clientId: null };
let buffer = [];
let seq = 0;
let currentScreen = null;
let flushHandler = null;
let started = false;

function genSessionId() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
  } catch (e) {
    /* noop */
  }
  return "sess-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

function configure(validationId, ctx = {}) {
  config = {
    validationId,
    sessionId: config.sessionId || genSessionId(),
    platform: "web",
    appVersion: ctx.appVersion || null,
    clientId: ctx.clientId || null,
  };
  started = true;
}

function isConfigured() {
  return started && !!config.validationId;
}

function getConfig() {
  return config;
}

function setScreen(screen) {
  currentScreen = screen != null ? String(screen) : null;
}

function getScreen() {
  return currentScreen;
}

function setFlushHandler(fn) {
  flushHandler = fn;
}

function track(type, data, opts = {}) {
  if (!started) return;
  // Diagnostics must NEVER break the eKYC flow — swallow any capture error.
  try {
    seq += 1;
    const event = {
      seq,
      ts: Date.now(),
      type,
      screen: opts.screen !== undefined ? opts.screen : currentScreen,
      data: data != null ? sanitize(data) : null,
    };
    buffer.push(event);
    if (buffer.length > BUFFER_CAP) buffer.splice(0, buffer.length - BUFFER_CAP);

    const critical = opts.critical || CRITICAL_TYPES.has(type);
    if (flushHandler && (critical || buffer.length >= BATCH_TRIGGER)) {
      try {
        flushHandler(critical);
      } catch (e) {
        /* transport must never break capture */
      }
    }
  } catch (e) {
    /* capture must never throw into the caller */
  }
}

function drainBatch(max = 100) {
  if (buffer.length === 0) return [];
  const batch = buffer.slice(0, max);
  buffer = buffer.slice(batch.length);
  return batch;
}

function peekAll() {
  return buffer.slice();
}

// Put events back at the front (failed send) — keep newest if we overflow.
function requeue(events) {
  if (!events || !events.length) return;
  buffer = events.concat(buffer);
  if (buffer.length > BUFFER_CAP) buffer.splice(0, buffer.length - BUFFER_CAP);
}

function size() {
  return buffer.length;
}

function buildPayload(events) {
  const s = diagnosticsStore.getSnapshot();
  return {
    sessionId: config.sessionId,
    ekycValidationId: config.validationId,
    platform: "web",
    appVersion: config.appVersion,
    // Client-authoritative, monotonic trigger counters (replay/double-send safe).
    counters: {
      serverTimeoutCount: s.serverTimeoutCount || 0,
      docScanFailCount: s.docScanFailCount || 0,
    },
    events,
  };
}

// ---- Contact-support counters (also mirrored into the on-row summary) ----
function recordServerTimeout(detail) {
  diagnosticsStore.incr("serverTimeoutCount");
  track("server_timeout", detail || {}, { critical: true });
}

function recordDocScanFailure(detail) {
  diagnosticsStore.incr("docScanFailCount");
  track("doc_scan_failed", detail || {}, { critical: true });
}

// Server-authoritative attempt counter (validationCount) — monotonic.
function syncDocScanFailCount(value, detail) {
  if (typeof value !== "number" || Number.isNaN(value)) return;
  const prev = diagnosticsStore.getSnapshot().docScanFailCount || 0;
  if (value > prev) {
    diagnosticsStore.raiseTo("docScanFailCount", value);
    track("doc_scan_failed", { ...(detail || {}), validationCount: value }, { critical: true });
  }
}

function reset() {
  buffer = [];
  seq = 0;
  currentScreen = null;
  started = false;
  config = { validationId: null, sessionId: null, platform: "web", appVersion: null, clientId: null };
}

export const collector = {
  configure,
  isConfigured,
  getConfig,
  setScreen,
  getScreen,
  setFlushHandler,
  track,
  drainBatch,
  peekAll,
  requeue,
  size,
  buildPayload,
  recordServerTimeout,
  recordDocScanFailure,
  syncDocScanFailCount,
  reset,
};
